import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, ReplaySubject, firstValueFrom, from, of, takeUntil } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import AlternateHost from 'entity/AlternateHost';
import AudioTrack from 'entity/AudioTrack';
import CategoryConfig from 'entity/CategoryConfig';
import Conversation from 'entity/Conversation';
import Playlist from 'entity/Playlist';
import Social from 'entity/Social';
import SocialAdmitted from 'entity/SocialAdmitted';
import SocialAttendee from 'entity/SocialAttendee';
import SocialMatch from 'entity/SocialMatch';
import SocialRoomSet from 'entity/SocialRoomSet';
import SocialSettings from 'entity/SocialSettings';
import SocialTheme from 'entity/SocialTheme';
import Paging from 'rest/Paging';
import SocialController from 'rest/SocialController';

import { socialAppActions, socialAppSelectors } from '../state/app';
import SocialThemeCategory from 'entity/SocialThemeCategory';

export interface NamedRoom {
  id: string;
  name: string;
  maxParticipants?: number;
  participantCount: number;
}

export type Rooms = NamedRoom[];

@Injectable({
  providedIn: 'root',
})
export class VideoChatService extends Loggable {
  private store = inject<Store<AppState>>(Store);

  private socialController = new SocialController();
  private region?: string;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private geoMusicAllowed?: boolean;

  private currentSocialId?: string;

  public constructor() {
    super();
    this.setDisplayName('VideoChatService');
    this.store.select(socialAppSelectors.getAuthenticatedSocialId).subscribe(({ authenticated, socialId }) => {
      // Avoid fetching until the user is logged in
      if (!authenticated) {
        return;
      }

      this.currentSocialId = socialId;
    });

    this.store
      .select(coreSelectors.getGeoMusicAllowed)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((allowed) => (this.geoMusicAllowed = allowed));
  }

  public initCurrentSocial(socialId: string) {
    this.info('initCurrentSocial fetching social attributes');
    this.store.dispatch(socialAppActions.FETCH_ROOM_SETTINGS_REQ({ socialId }));
  }

  public getSocial(socialId: string, allocateServer?: boolean, region?: string): Observable<Social> {
    return from(this.socialController.getSocial(socialId, allocateServer, region));
  }

  public getSocialPromise(socialId: string, allocateServer?: boolean, region?: string): Promise<Social> {
    return this.socialController.getSocial(socialId, allocateServer, region);
  }

  public getSocialRoomSet(roomSetId: string): Observable<SocialRoomSet> {
    return from(this.socialController.getSocialRoomSet(roomSetId));
  }

  public getSocialRoomSetPromise(roomSetId: string): Promise<SocialRoomSet> {
    return this.socialController.getSocialRoomSet(roomSetId);
  }

  public getSocialRoomSetSettings(roomSetId: string): Observable<SocialSettings> {
    return from(this.socialController.getSocialRoomSetSettings(roomSetId));
  }

  public startSocial(socialId: string, region?: string): Observable<Social> {
    return from(this.socialController.startSocial(socialId, region));
  }

  public joinSocial(socialId: string): Observable<SocialAttendee> {
    return from(this.socialController.joinSocial(socialId));
  }

  public addSocialHost(socialId: string, hostUserId: string): Observable<SocialAttendee> {
    return from(this.socialController.addSocialHost(socialId, hostUserId));
  }

  public canJoinSocial(socialId: string): Observable<boolean> {
    return from(this.socialController.canJoinSocial(socialId));
  }

  public cancelSocial(socialId?: string): Observable<Social> {
    if (socialId === undefined) {
      return of({} as Social);
    }
    return from(this.socialController.cancelSocial(socialId));
  }

  public async getAllPlaylists(page?: Paging): Promise<Playlist[]> {
    const playlists = (await firstValueFrom(from(this.socialController.getAllPlaylists(page)))) ?? [];
    const tempPlaylistOptions: Playlist[] = [];

    playlists.forEach((playlist) => {
      if (playlist.type === 0) {
        tempPlaylistOptions.push({
          ...playlist,
          available: playlist.available || this.geoMusicAllowed,
        });
      }
    });

    return tempPlaylistOptions;
  }

  public getCategories(page?: Paging): Observable<CategoryConfig[]> {
    return from(this.socialController.getCategories(page));
  }

  public getConversations(categoryId: number, page?: Paging): Observable<Conversation[]> {
    return from(this.socialController.getConversations(categoryId, page));
  }

  public leaveSocial(socialId: string): Observable<SocialAttendee> {
    return from(this.socialController.leaveSocial(socialId));
  }

  public updateSocial(social: Social): Observable<Social> {
    return from(this.socialController.updateSocial(social));
  }

  public matchSocial(matchId: string): Observable<SocialMatch> {
    return from(this.socialController.matchSocial(matchId));
  }

  public getFrameRate(): Observable<number> {
    return from(this.socialController.getFrameRate());
  }

  public hasCompletedDemo(): Observable<boolean> {
    return from(this.socialController.hasCompletedDemo());
  }

  public setDemoCompleted(): Observable<void> {
    return from(this.socialController.setDemoCompleted());
  }

  public getMaxSavedCount(): Promise<number> {
    return this.socialController.getMaxSavedCount();
  }

  public getThemesForCategory(socialThemeCategoryId: string, page?: Paging): Promise<SocialTheme[]> {
    return this.socialController.getThemesForCategory(socialThemeCategoryId, page);
  }

  public getThemeCategories(page?: Paging): Promise<SocialThemeCategory[]> {
    return this.socialController.getThemeCategories(page);
  }

  public saveSocialSettings(socialId: string, settings: SocialSettings): void {
    this.store.dispatch(socialAppActions.SAVE_ROOM_SETTINGS_REQ({ socialId, settings }));
  }

  public checkAdmittedStatus(socialId: string, notifyHosts?: boolean): Promise<SocialAdmitted> {
    return this.socialController.checkAdmittedStatus(socialId, notifyHosts);
  }

  public setAdmittedStatus(socialId: string, userId: string, admit: boolean, ban: boolean): Promise<SocialAdmitted> {
    return this.socialController.setAdmittedStatus(socialId, userId, admit, ban);
  }

  public setAdmittedStatuses(
    socialId: string,
    userIds: string[],
    admit: boolean,
    ban: boolean,
  ): Promise<SocialAdmitted[]> {
    return this.socialController.setAdmittedStatuses(socialId, userIds, admit, ban);
  }

  public async getRegion(): Promise<string> {
    if (this.region) {
      return this.region;
    }
    this.region = await this.pingRegion().catch(() => {
      this.error('Failed to get region from ping, defaulting to us-east-2');
      return 'us-east-2';
    });
    return this.region;
  }

  private pingRegion(): Promise<string> {
    return new Promise<string>((resolve) => {
      const url = 'https://region-lat.scoot.app/api/v1/region';
      const request = new XMLHttpRequest();
      request.ontimeout = () => {
        this.error('Region request timed out for url ', url);
        resolve('us-east-2');
      };
      request.onload = () => {
        if (request.readyState === 4) {
          if (request.status === 200) {
            const { region } = JSON.parse(request.response) as { region: string };
            resolve(region);
          } else {
            this.error('Region request failed with status ', request.status);
            resolve('us-east-2');
          }
        }
      };
      request.open('GET', url, true);
      request.timeout = 5000;
      request.send(null);
    });
  }

  public getAlternateHostCandidates({
    paging,
    socialId,
    text,
  }: {
    socialId: string;
    paging: Paging;
    text: string;
  }): Promise<AlternateHost[]> {
    return this.socialController.getAlternateHostCandidates(socialId, text, paging);
  }

  public getAlternateRoomSetHostCandidates({
    paging,
    socialRoomSetId,
    text,
  }: {
    socialRoomSetId: string;
    paging: Paging;
    text: string;
  }): Promise<AlternateHost[]> {
    return this.socialController.getAlternateRoomSetHostCandidates(socialRoomSetId, text, paging);
  }

  public async updateAlternateHosts(socialId: string, hosts: AlternateHost[]): Promise<void> {
    return await this.socialController.updateAlternateHosts(socialId, hosts);
  }

  public async updateAlternateRoomSetHosts(socialRoomSetId: string, hosts: AlternateHost[]): Promise<void> {
    return await this.socialController.updateAlternateRoomSetHosts(socialRoomSetId, hosts);
  }

  public destroy(): void {
    this.currentSocialId = undefined;
    this.region = undefined;
  }

  public async getFirstTrackFromPlaylist(playlistId?: string): Promise<AudioTrack | undefined> {
    if (!playlistId) {
      return;
    }
    const audioTrack = (await firstValueFrom(from(this.socialController.getAudioTracks(playlistId)))) ?? [];

    return audioTrack[0];
  }

  public async getPlaylistById(playlistId?: string): Promise<Playlist | undefined> {
    if (!playlistId) {
      return;
    }

    return await this.socialController.getPlaylist(playlistId);
  }
}
